import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import type { MarketplaceProvider } from '../../types';
import { getCoords, onCoordsChanged, requestCoords, type Coords } from '../../utils/patientLocation';
import { DoctorCard, IconLocation, IconX, offersOnline } from './DoctorCard';

// ─── Inline SVG Icons ────────────────────────────────────────────────────────

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <div
        style={{
          width: 36,
          height: 36,
          border: `3px solid ${T.border}`,
          borderTop: `3px solid ${T.teal}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// ─── Constants ───────────────────────────────────────────────────────────────

const SPECIALTIES = [
  'All',
  'General Physician',
  'Cardiologist',
  'Dermatologist',
  'Pediatrician',
  'Gynecologist',
  'Orthopedic',
  'Neurologist',
  'ENT',
  'Dentist',
  'Psychiatrist',
];

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({ query, loadError }: { query: string; loadError: boolean }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px' }}>
      <div style={{ marginBottom: 16 }}>
        <svg width={56} height={56} viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto', display: 'block' }}>
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
      </div>
      <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>
        {loadError ? 'Could not load doctors' : 'No doctors found'}
      </div>
      <div style={{ fontFamily: T.font, fontSize: 13, color: T.textSec }}>
        {loadError
          ? 'Please check your connection and try again.'
          : query
            ? `No results for "${query}"`
            : 'Try a different specialty or search term'}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DoctorDiscovery() {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  // Deep links from search/care-intelligence carry ?specialty= / ?search= —
  // honour them instead of silently dropping the user on the unfiltered list.
  const [search, setSearch] = useState(urlParams.get('search') || '');
  const [selectedSpecialty, setSelectedSpecialty] = useState(urlParams.get('specialty') || 'All');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [onlineConsult, setOnlineConsult] = useState(false);
  const [sortBy, setSortBy] = useState<'relevance' | 'rating' | 'price_low' | 'price_high'>('relevance');
  const [providers, setProviders] = useState<MarketplaceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // ── Saved doctors — `GET /favorites/ids`, the same list the app's heart
  //    writes to. A failure leaves every heart empty rather than guessing.
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    patientApi.getFavoriteIds()
      .then((ids) => { if (alive) setSavedIds(new Set(ids ?? [])); })
      .catch(() => { if (alive) setSavedIds(new Set()); });
    return () => { alive = false; };
  }, []);

  const toggleSaved = async (p: MarketplaceProvider) => {
    const wasSaved = savedIds.has(p.id);
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (wasSaved) next.delete(p.id); else next.add(p.id);
      return next;
    });
    setSaveError(null);
    try {
      if (wasSaved) await patientApi.removeFavorite(p.id);
      else await patientApi.addFavorite(p.id);
    } catch {
      // Put the heart back: a save the server refused is not a save.
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(p.id); else next.delete(p.id);
        return next;
      });
      setSaveError('Could not update your saved doctors. Please try again.');
    }
  };

  // ── Distance — only ever shown when the patient has shared their position
  //    AND the directory holds coordinates for the practice.
  const [coords, setCoords] = useState<Coords | null>(() => getCoords());
  const [locationAsked, setLocationAsked] = useState(false);
  const [locationRefused, setLocationRefused] = useState(false);
  useEffect(() => onCoordsChanged(setCoords), []);

  const askForLocation = async () => {
    setLocationAsked(true);
    const c = await requestCoords();
    setCoords(c);
    setLocationRefused(!c);
  };
  const anyProviderHasCoords = providers.some(
    (p) => typeof p.latitude === 'number' && typeof p.longitude === 'number',
  );

  const fetchProviders = async (searchVal: string, specialty: string, verified: boolean) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { type: 'DOCTOR', limit: 20 };
      if (searchVal.trim()) params.search = searchVal.trim();
      if (verified) params.verified = 'true';
      if (specialty !== 'All') params.search = specialty + (searchVal.trim() ? ' ' + searchVal.trim() : '');
      const res = await patientApi.getMarketplaceProviders(params);
      let results = res.providers || [];
      if (onlineConsult) {
        results = results.filter(offersOnline);
      }
      if (aliveRef.current) { setProviders(results); setLoadError(false); }
    } catch {
      if (aliveRef.current) { setProviders([]); setLoadError(true); }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProviders(search, selectedSpecialty, verifiedOnly);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, selectedSpecialty, verifiedOnly, onlineConsult]);

  return (
    <div
      style={{
        background: T.bg,
        fontFamily: T.font,
        margin: '0 auto',
        paddingBottom: 100,
      }}
    >
      {/* ── Header ───────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(145deg, ${T.navy} 0%, #003670 100%)`,
          padding: '36px 16px 24px',
          position: 'relative',
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1 style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.white, margin: 0, lineHeight: 1.2 }}>
            Find a Doctor
          </h1>
          <p style={{ fontFamily: T.font, fontSize: 11, color: 'rgba(255,255,255,0.65)', margin: 0, marginTop: 2 }}>
            Verified specialists near you
          </p>
        </div>

        {/* Search bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: T.white,
            borderRadius: 14,
            padding: '11px 14px',
            boxShadow: T.shadowMd,
          }}
        >
          <IconSearch color={T.textTer} />
          <input
            type="text"
            placeholder="Search doctors, specialty, symptoms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              background: 'transparent',
              padding: 0,
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
            >
              <IconX color={T.textTer} />
            </button>
          )}
        </div>
      </div>

      {/* ── Specialty Chips ───────────────────────────────────── */}
      <div style={{ padding: '14px 16px 0', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content', paddingBottom: 4 }}>
          {SPECIALTIES.map((spec) => {
            const isActive = selectedSpecialty === spec;
            return (
              <button
                key={spec}
                onClick={() => setSelectedSpecialty(spec)}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: isActive ? T.white : T.textSec,
                  background: isActive ? T.navy : T.white,
                  border: `1.5px solid ${isActive ? T.navy : T.border}`,
                  borderRadius: 20,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                  boxShadow: isActive ? `0 2px 8px ${T.navy}30` : T.shadow,
                }}
              >
                {spec}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Quick Filter Toggles ──────────────────────────────── */}
      <div style={{ padding: '10px 16px 0', display: 'flex', gap: 8 }}>
        {/* Verified only toggle */}
        <button
          onClick={() => setVerifiedOnly((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 600,
            color: verifiedOnly ? T.teal : T.textSec,
            background: verifiedOnly ? T.teal + '12' : T.white,
            border: `1.5px solid ${verifiedOnly ? T.teal : T.border}`,
            borderRadius: 20,
            padding: '7px 13px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: verifiedOnly ? T.teal : T.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s ease',
            }}
          >
            {verifiedOnly && (
              <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </div>
          Verified only
        </button>

        {/* Online consult toggle */}
        <button
          onClick={() => setOnlineConsult((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 600,
            color: onlineConsult ? T.purple : T.textSec,
            background: onlineConsult ? T.purple + '12' : T.white,
            border: `1.5px solid ${onlineConsult ? T.purple : T.border}`,
            borderRadius: 20,
            padding: '7px 13px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: onlineConsult ? T.purple : T.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s ease',
            }}
          >
            {onlineConsult && (
              <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </div>
          Online consult
        </button>

        {/* Distance opt-in. The browser is the only thing that knows where the
            patient is, and it asks before telling us. */}
        {!coords && (
          <button
            onClick={askForLocation}
            disabled={locationAsked && !locationRefused}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: T.font,
              fontSize: 12,
              fontWeight: 600,
              color: locationRefused ? T.textTer : T.textSec,
              background: T.white,
              border: `1.5px solid ${T.border}`,
              borderRadius: 20,
              padding: '7px 13px',
              cursor: locationRefused ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <IconLocation color={locationRefused ? T.textTer : T.teal} />
            {locationRefused ? 'Location off' : 'Show distance'}
          </button>
        )}
      </div>

      {/* Said plainly rather than leaving the distance quietly absent. */}
      {coords && !anyProviderHasCoords && !loading && providers.length > 0 && (
        <div style={{ padding: '8px 16px 0', fontFamily: T.font, fontSize: 11, color: T.textTer }}>
          Distance is on, but none of these practices has published its location yet.
        </div>
      )}
      {saveError && (
        <div style={{ padding: '8px 16px 0', fontFamily: T.font, fontSize: 11, color: T.warning }}>{saveError}</div>
      )}

      {/* ── Results count & Sort ────────────────────────────────── */}
      {!loading && (
        <div style={{ padding: '14px 16px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>
            {providers.length} {providers.length === 1 ? 'Doctor' : 'Doctors'} found
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            style={{
              fontFamily: T.font,
              fontSize: 11,
              fontWeight: 600,
              color: T.navy,
              background: T.white,
              border: `1.5px solid ${T.border}`,
              borderRadius: 8,
              padding: '5px 8px',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="relevance">Relevance</option>
            <option value="rating">Rating</option>
            <option value="price_low">Price: Low-High</option>
            <option value="price_high">Price: High-Low</option>
          </select>
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────── */}
      <div style={{ padding: '4px 16px 0' }}>
        {loading ? (
          <Spinner />
        ) : providers.length === 0 ? (
          <EmptyState query={search} loadError={loadError} />
        ) : (
          [...providers].sort((a, b) => {
            if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
            if (sortBy === 'price_low' || sortBy === 'price_high') {
              const getPrice = (p: MarketplaceProvider) => {
                if (!p.pricing) return 0;
                const list = typeof p.pricing === 'string' ? JSON.parse(p.pricing) : p.pricing;
                return Array.isArray(list) && list.length > 0 ? list[0].price : 0;
              };
              return sortBy === 'price_low' ? getPrice(a) - getPrice(b) : getPrice(b) - getPrice(a);
            }
            return 0;
          }).map((p) => (
            <DoctorCard
              key={p.id}
              provider={p}
              coords={coords}
              isSaved={savedIds.has(p.id)}
              onToggleSave={() => void toggleSaved(p)}
              onViewProfile={() => navigate(`/patient/care-services/doctors/${p.slug}`)}
              onBook={() => navigate(`/patient/care-services/doctors/${p.slug}/book`)}
            />
          ))
        )}
      </div>

      {/* ── Branding Footer ───────────────────────────────── */}
      {!loading && (
        <div style={{ padding: '20px 16px 0', textAlign: 'center' }}>
          <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
            NeoHealthCard™ · Verified provider network · Every booking is consent-based and audit logged
          </span>
        </div>
      )}
    </div>
  );
}
